/**
 * @desc: SortBy 组件演示文件，展示直接排序和选项排序两种模式
 */
import React, { useState } from 'react';
import SortBy from './index';
import type { ISortByProps } from './index';
import { SortType } from '@home/enums/common';

import type { ISortState, ISortOption } from './types';

/**
 * 直接排序模式 Demo
 * 点击图标在「降序 → 升序 → 无排序」之间循环切换
 */
const DirectModeDemo: React.FC = () => {
  const [sorts, setSorts] = useState<ISortState[]>([]);

  const handleSortsChange: ISortByProps['onSortsChange'] = (newSorts) => {
    setSorts(newSorts);
  };

  const currentSortType = sorts?.[0]?.type ?? SortType.None;

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        当前排序方式：<strong>{currentSortType || '无'}</strong>
      </div>
      <SortBy
        field="name"
        title="游戏名称"
        useResetSortTypeNone
        sorts={sorts}
        onSortsChange={handleSortsChange}
      />
    </div>
  );
};

/**
 * 选项排序模式 Demo
 * 点击图标弹出浮窗，在浮窗内选择排序选项触发排序
 */
const OptionsModeDemo: React.FC = () => {
  const sortOptions: Readonly<ISortOption[]> = [
    { field: 'revenue', text: '按收入排序' },
    { field: 'downloads', text: '按下载量排序' },
    { field: 'dau', text: '按日活排序' },
  ];

  const [sorts, setSorts] = useState<ISortState[]>([]);

  const handleSortsChange: ISortByProps['onSortsChange'] = (newSorts) => {
    setSorts(newSorts);
  };

  const currentSort = sorts?.[0];
  const currentType = currentSort?.type ?? SortType.None;
  const currentOption = currentSort?.option;

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        当前排序方式：<strong>{currentType || '无'}</strong>
        {currentOption && <span>（{currentOption.text}）</span>}
      </div>
      <SortBy
        field="metric"
        title="指标"
        useResetSortTypeNone
        options={sortOptions}
        sorts={sorts}
        onSortsChange={handleSortsChange}
      />
    </div>
  );
};

/**
 * Demo 案例配置
 */
export const demoConfig = [
  {
    title: '基础用法（直接排序模式）',
    description:
      '不传 options 或 options 为空数组/单元素时，使用 DirectMode 直接排序模式。点击排序图标在升序、降序、无排序之间切换，useResetSortTypeNone=true 时允许回到无排序状态。',
    code: `<SortBy
  field="name"
  title="游戏名称"
  useResetSortTypeNone
  sorts={sorts}
  onSortsChange={handleSortsChange}
/>`,
    element: <DirectModeDemo />,
  },
  {
    title: '选项排序模式',
    description:
      '传入 options 数组（长度大于 1）时，使用 OptionsMode 选项排序模式。点击排序图标弹出浮窗，在浮窗内选择排序选项后触发对应字段的排序。',
    code: `<SortBy
  field="metric"
  title="指标"
  useResetSortTypeNone
  options={[
    { field: 'revenue', text: '按收入排序' },
    { field: 'downloads', text: '按下载量排序' },
    { field: 'dau', text: '按日活排序' },
  ]}
  sorts={sorts}
  onSortsChange={handleSortsChange}
/>`,
    element: <OptionsModeDemo />,
  },
];

export default demoConfig;
